import { useEffect, useRef, useCallback } from 'react';
import { SERIAL_PORT_FILTERS, isGogglePort } from '../services/espFlasher';

/**
 * Watch for goggle serial ports appearing and disappearing.
 *
 * While `active`, reports every permitted goggle port already plugged in, then
 * every port that connects or disconnects afterwards. Chrome only reports ports
 * this site is allowed to use without a picker: ports picked before, or every
 * Espressif port when the SerialAllowUsbDevicesForUrls policy is installed.
 *
 * @param {{active: boolean,
 *          onConnect: (port: SerialPort) => void,
 *          onDisconnect: (port: SerialPort) => void}} options
 * @returns {{addPortManually: () => Promise<void>}} addPortManually shows the
 *   port picker, for stations without the Chrome policy.
 */
const useSerialPortWatcher = ({ active, onConnect, onDisconnect }) => {
  // Callbacks change every render; listeners read the latest through refs.
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  onConnectRef.current = onConnect;
  onDisconnectRef.current = onDisconnect;

  useEffect(() => {
    if (!active || !('serial' in navigator)) return;
    let cancelled = false;

    const handleConnect = (event) => {
      const port = event.target;
      if (isGogglePort(port)) onConnectRef.current(port);
    };
    const handleDisconnect = (event) => {
      const port = event.target;
      if (isGogglePort(port)) onDisconnectRef.current(port);
    };

    navigator.serial.addEventListener('connect', handleConnect);
    navigator.serial.addEventListener('disconnect', handleDisconnect);

    navigator.serial.getPorts().then((ports) => {
      if (cancelled) return;
      ports.filter(isGogglePort).forEach(port => onConnectRef.current(port));
    });

    return () => {
      cancelled = true;
      navigator.serial.removeEventListener('connect', handleConnect);
      navigator.serial.removeEventListener('disconnect', handleDisconnect);
    };
  }, [active]);

  const addPortManually = useCallback(async () => {
    let port;
    try {
      port = await navigator.serial.requestPort({ filters: SERIAL_PORT_FILTERS });
    } catch (e) {
      return; // Picker cancelled.
    }
    onConnectRef.current(port);
  }, []);

  return { addPortManually };
};

export default useSerialPortWatcher;
